import type { PaginatedNode } from "./PaginatedNode";
import type { ID, PageFetcher, PageTrimOp } from "./types";
import { hasEdgeHit } from "./pagination.utils";

/**
 * Inputs to a single `planTrims` call. The planner is a pure function of
 * these inputs — no I/O, no tree mutation, no internal state.
 */
export interface TrimPlannerArgs {
  readonly root: PaginatedNode;
  readonly visibleIds: ReadonlySet<ID>;
  readonly cleanedCollapsed: ReadonlySet<ID>;
  readonly maxDepth: number;
  readonly maxPages: number;
  readonly getFetcher: (depth: number) => PageFetcher;
}

/** Position of a single anchor in the per-depth node sequence. */
interface AnchorPosition {
  readonly nodeIdx: number;
  readonly pageIdx: number;
}

/** Pair of anchors found per depth: top (first visible page) and bottom (last). */
interface AnchorPair {
  readonly topAnchor: AnchorPosition;
  readonly bottomAnchor: AnchorPosition;
}

interface CountItemsBeyondArgs {
  readonly ctx: TrimPlannerArgs;
  readonly side: "above" | "below";
  readonly anchor: AnchorPosition;
  readonly nodesAtDepth: PaginatedNode[];
}

interface EmitTrimsArgs {
  readonly ctx: TrimPlannerArgs;
  readonly trims: PageTrimOp[];
  readonly itemsExcess: number;
  readonly anchor: AnchorPosition;
  readonly nodesAtDepth: PaginatedNode[];
}

interface HasEdgeHitArgs {
  readonly ctx: TrimPlannerArgs;
  readonly side: "above" | "below";
  readonly nodesAtDepth: PaginatedNode[];
  readonly threshold: number;
}

/**
 * Compute trim operations per depth under the dual-anchor model.
 *
 * Per-depth budget is `config[depth].pageSize * maxPages` ITEMS — so
 * `maxPages = 1` keeps roughly one page worth of records on either
 * side of the anchor span, regardless of how many backend pages those
 * records were split into. Trim ops are still page-granular.
 *
 * For each depth the algorithm finds TWO anchors: the FIRST page (doc
 * order) holding a visible record (`topAnchor`) and the LAST such
 * page (`bottomAnchor`). `itemsBeyond` counts records strictly above
 * the top-anchor and strictly below the bottom-anchor; if either
 * exceeds the budget, whole pages are removed from the far end until
 * the running excess is covered.
 *
 * Hysteresis gates (mirror, items-aware) use the SAME budget as the
 * trim threshold — one per-depth knob, no separate `edgeThreshold`
 * for trims:
 *   - Above-side trim requires `hasNearBottomHit` — visible inside
 *     the last `budget` items of the loaded sequence at this depth.
 *   - Below-side trim requires `hasNearTopHit` — visible inside the
 *     first `budget` items of the loaded sequence.
 *
 * Collapsed nodes are skipped on both scans — their records stay
 * resident so collapse→re-expand requires no re-fetch.
 */
export function planTrims(args: TrimPlannerArgs): PageTrimOp[] {
  const trims: PageTrimOp[] = [];
  const nodesByDepth = getNodesByDepth(args.root);

  for (let depth = 0; depth <= args.maxDepth; depth++) {
    const nodesAtDepth = nodesByDepth.get(depth) ?? [];
    if (nodesAtDepth.length === 0) {
      continue;
    }

    const anchors = findAnchors(args, nodesAtDepth);
    if (!anchors) {
      continue;
    }

    // Single source of truth for this depth: the items budget. It
    // sets both "how many items can stay beyond the anchor" AND the
    // hysteresis distance for the trim-gate. The load-trigger uses
    // `edgeThreshold` separately (see `classifyNodeFlow`).
    const budget = itemBudgetAtDepth(args, depth);

    const itemsAbove = countItemsBeyond({
      ctx: args,
      side: "above",
      anchor: anchors.topAnchor,
      nodesAtDepth,
    });
    const itemsBelow = countItemsBeyond({
      ctx: args,
      side: "below",
      anchor: anchors.bottomAnchor,
      nodesAtDepth,
    });
    const aboveExcess = itemsAbove - budget;
    const belowExcess = itemsBelow - budget;
    const gateAbove =
      aboveExcess > 0 &&
      hasEdgeHitAtDepth({ ctx: args, side: "above", nodesAtDepth, threshold: budget });
    const gateBelow =
      belowExcess > 0 &&
      hasEdgeHitAtDepth({ ctx: args, side: "below", nodesAtDepth, threshold: budget });

    if (gateAbove) {
      emitAboveTrims({
        ctx: args,
        trims,
        itemsExcess: aboveExcess,
        anchor: anchors.topAnchor,
        nodesAtDepth,
      });
    }
    if (gateBelow) {
      emitBelowTrims({
        ctx: args,
        trims,
        itemsExcess: belowExcess,
        anchor: anchors.bottomAnchor,
        nodesAtDepth,
      });
    }
  }

  return trims;
}

/** Single-pass dual-anchor scan at one depth. */
function findAnchors(ctx: TrimPlannerArgs, nodesAtDepth: PaginatedNode[]): AnchorPair | null {
  let topNodeIdx = -1;
  let topPageIdx = -1;
  let bottomNodeIdx = -1;
  let bottomPageIdx = -1;

  for (let nodeIdx = 0; nodeIdx < nodesAtDepth.length; nodeIdx++) {
    const node = nodesAtDepth[nodeIdx];
    if (ctx.cleanedCollapsed.has(node.id)) {
      continue;
    }
    for (let pageIdx = 0; pageIdx < node.pages.pages.length; pageIdx++) {
      const page = node.pages.pages[pageIdx];
      if (!page.itemIds.some((id) => ctx.visibleIds.has(id))) {
        continue;
      }
      if (topNodeIdx < 0) {
        topNodeIdx = nodeIdx;
        topPageIdx = pageIdx;
      }
      bottomNodeIdx = nodeIdx;
      bottomPageIdx = pageIdx;
    }
  }

  if (topNodeIdx < 0) {
    return null;
  }

  return {
    topAnchor: { nodeIdx: topNodeIdx, pageIdx: topPageIdx },
    bottomAnchor: { nodeIdx: bottomNodeIdx, pageIdx: bottomPageIdx },
  };
}

/**
 * Sum of items in pages strictly beyond the anchor on the given side,
 * counted across all non-collapsed nodes at this depth. The unit of
 * comparison against the depth's budget is items, not pages.
 */
function countItemsBeyond(args: CountItemsBeyondArgs): number {
  const { ctx, side, anchor, nodesAtDepth } = args;
  const anchorNode = nodesAtDepth[anchor.nodeIdx];
  let items = 0;

  if (side === "below") {
    for (let pageIdx = anchor.pageIdx + 1; pageIdx < anchorNode.pages.pages.length; pageIdx++) {
      items += anchorNode.pages.pages[pageIdx].itemIds.length;
    }
    for (let nodeIdx = anchor.nodeIdx + 1; nodeIdx < nodesAtDepth.length; nodeIdx++) {
      items += itemsInNonCollapsedNode(ctx, nodesAtDepth[nodeIdx]);
    }
    return items;
  }

  // side === "above"
  for (let pageIdx = 0; pageIdx < anchor.pageIdx; pageIdx++) {
    items += anchorNode.pages.pages[pageIdx].itemIds.length;
  }
  for (let nodeIdx = 0; nodeIdx < anchor.nodeIdx; nodeIdx++) {
    items += itemsInNonCollapsedNode(ctx, nodesAtDepth[nodeIdx]);
  }
  return items;
}

/** Total record count in a node, treating collapsed nodes as zero. */
function itemsInNonCollapsedNode(ctx: TrimPlannerArgs, node: PaginatedNode): number {
  if (ctx.cleanedCollapsed.has(node.id)) {
    return 0;
  }
  let items = 0;
  for (const page of node.pages.pages) {
    items += page.itemIds.length;
  }
  return items;
}

/**
 * Mirror of `emitBelowTrims` for the above side: trim whole pages from
 * the far-above end (farthest from the top anchor) toward the anchor
 * until the running item-count covers `itemsExcess`.
 *
 * Skip rules per node (in order):
 *   - Collapsed nodes (`cleanedCollapsed`) — their items count as zero
 *     in `countItemsBeyond`, so trimming them would over-count.
 *   - Non-anchor nodes whose header is in `visibleIds` — protects a
 *     freshly bootstrapped neighbour from being evicted on the next
 *     cycle (see `isHeaderVisible`). The anchor node itself is exempt;
 *     its trimmable range is `[0 .. topAnchor.pageIdx - 1]` which
 *     never overlaps a page that holds a visible record.
 */
function emitAboveTrims(args: EmitTrimsArgs): void {
  const { ctx, trims, itemsExcess, anchor: topAnchor, nodesAtDepth } = args;
  let remaining = itemsExcess;
  for (let nodeIdx = 0; nodeIdx <= topAnchor.nodeIdx && remaining > 0; nodeIdx++) {
    const node = nodesAtDepth[nodeIdx];
    if (ctx.cleanedCollapsed.has(node.id)) {
      continue;
    }
    const isAnchorNode = nodeIdx === topAnchor.nodeIdx;
    if (!isAnchorNode && isHeaderVisible(ctx, node)) {
      continue;
    }
    const lastTrimmableIdx = isAnchorNode ? topAnchor.pageIdx - 1 : node.pages.pages.length - 1;
    for (let pageIdx = 0; pageIdx <= lastTrimmableIdx && remaining > 0; pageIdx++) {
      const pageItems = node.pages.pages[pageIdx].itemIds.length;
      trims.push({ nodeId: node.id, from: "top", position: "above-viewport" });
      remaining -= pageItems;
    }
  }
}

/**
 * Trim whole pages from the far-below end (farthest from the bottom
 * anchor) toward the anchor until the running item-count covers
 * `itemsExcess`. Granularity is always one page — we cannot fetch
 * fractional pages back, so we cannot remove fractional pages either.
 *
 * Skip rules per node (in order):
 *   - Collapsed nodes (`cleanedCollapsed`) — their items count as zero
 *     in `countItemsBeyond`, so trimming them would over-count.
 *   - Non-anchor nodes whose header is in `visibleIds` — protects a
 *     freshly bootstrapped neighbour from being evicted on the next
 *     cycle (see `isHeaderVisible`). The anchor node itself is exempt;
 *     its trimmable range is `[bottomAnchor.pageIdx + 1 .. last]`
 *     which never overlaps a page that holds a visible record.
 */
function emitBelowTrims(args: EmitTrimsArgs): void {
  const { ctx, trims, itemsExcess, anchor: bottomAnchor, nodesAtDepth } = args;
  let remaining = itemsExcess;
  for (
    let nodeIdx = nodesAtDepth.length - 1;
    nodeIdx >= bottomAnchor.nodeIdx && remaining > 0;
    nodeIdx--
  ) {
    const node = nodesAtDepth[nodeIdx];
    if (ctx.cleanedCollapsed.has(node.id)) {
      continue;
    }
    const isAnchorNode = nodeIdx === bottomAnchor.nodeIdx;
    if (!isAnchorNode && isHeaderVisible(ctx, node)) {
      continue;
    }
    const lastTrimmableIdx = node.pages.pages.length - 1;
    const firstTrimmableIdx = isAnchorNode ? bottomAnchor.pageIdx + 1 : 0;
    for (let pageIdx = lastTrimmableIdx; pageIdx >= firstTrimmableIdx && remaining > 0; pageIdx--) {
      const pageItems = node.pages.pages[pageIdx].itemIds.length;
      trims.push({ nodeId: node.id, from: "bottom", position: "below-viewport" });
      remaining -= pageItems;
    }
  }
}

/**
 * Skip trimming a NON-ANCHOR node whose header is currently in the viewport.
 *
 * `descendIntoChildren` uses the SAME signal (`visibleIds.has(childId)`)
 * to bootstrap an empty sibling — protecting it here keeps load-side
 * and trim-side semantics aligned. Without this, a freshly bootstrapped
 * neighbour group can be evicted on the very next cycle while its
 * header is still on screen, producing a bootstrap → trim → bootstrap
 * loop that fires once per scroll-driven re-render.
 *
 * The anchor node is EXEMPT: it has visible records by definition, and a
 * sticky/pinned header keeps its id in `visibleIds` while the user scrolls
 * deep inside that node. Skipping it would prevent its own out-of-window
 * pages from ever being trimmed, letting one group grow unbounded past
 * the per-depth item budget. The anchor node's trimmable range is already
 * scoped to pages strictly before/after the anchor pageIdx, so trimming
 * never evicts a page that holds a visible record.
 */
function isHeaderVisible(ctx: TrimPlannerArgs, node: PaginatedNode): boolean {
  return ctx.visibleIds.has(node.id);
}

/** True when any non-collapsed node at this depth has a visible id within `threshold`. */
function hasEdgeHitAtDepth(args: HasEdgeHitArgs): boolean {
  const { ctx, side, nodesAtDepth, threshold } = args;
  return nodesAtDepth.some(
    (node) =>
      !ctx.cleanedCollapsed.has(node.id) &&
      node.pages.length &&
      hasEdgeHit(side, node, ctx.visibleIds, threshold),
  );
}

/** Per-depth budget in items: `pageSize * maxPages` for the depth's fetcher. */
function itemBudgetAtDepth(ctx: TrimPlannerArgs, depth: number): number {
  return ctx.getFetcher(depth).pageSize * ctx.maxPages;
}

/** Group every materialized node by its depth, in DFS document order. */
function getNodesByDepth(root: PaginatedNode): Map<number, PaginatedNode[]> {
  const result = new Map<number, PaginatedNode[]>();
  result.set(0, [root]);
  collectIntoDepthMap(root, result);
  return result;
}

/** DFS helper: append `node`'s descendants into `result`, keyed by depth. */
function collectIntoDepthMap(node: PaginatedNode, result: Map<number, PaginatedNode[]>): void {
  for (const page of node.pages.pages) {
    for (const childId of page.itemIds) {
      const child = node.getChild(childId);
      if (!child) {
        continue;
      }
      let list = result.get(child.depth);
      if (!list) {
        list = [];
        result.set(child.depth, list);
      }
      list.push(child);
      collectIntoDepthMap(child, result);
    }
  }
}
